import React from "react";
import { motion } from "framer-motion";
import { InspectionGridItem } from "../types";
import { InspectionCardHeader } from "./InspectionCardHeader";
import { InspectionCardTabs } from "./InspectionCardTabs";
import { InspectionCardContent } from "./InspectionCardContent";
import { InspectionCardFooter } from "./InspectionCardFooter";

interface InspectionCardProps {
  gridItem: InspectionGridItem;
  gridIndex: number;
  gridItemsLength: number;
  isDarkMode: boolean;
  updatingStatus: boolean;
  contentRef: (el: HTMLDivElement | null) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onTabChange: (tabIndex: number) => void;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onOpenDetail: () => void;
  onDownloadReport: () => void;
  onConfirmClose: () => void;
}

export const InspectionCard = React.forwardRef<
  HTMLDivElement,
  InspectionCardProps
>(
  (
    {
      gridItem,
      gridIndex,
      gridItemsLength,
      isDarkMode,
      updatingStatus,
      contentRef,
      onMouseEnter,
      onMouseLeave,
      onTabChange,
      onScroll,
      onOpenDetail,
      onDownloadReport,
      onConfirmClose,
    },
    ref
  ) => {
    const getItemClass = (count: number): string => {
      if (count === 1) return "w-full h-full max-h-[900px]";
      if (count === 2) return "w-full h-full max-h-[850px]";
      if (count === 3) return "w-full h-full max-h-[850px]";
      if (count === 4) return "w-full h-full max-h-[700px]";
      if (count <= 6) return "w-full h-full max-h-[700px]";
      if (count <= 9) return "w-full h-full max-h-[550px]";
      if (count <= 12) return "w-full h-full max-h-[500px]";
      if (count <= 16) return "w-full h-full max-h-[450px]";
      return "w-full h-full max-h-[400px]";
    };

    return (
      <motion.div
        ref={ref}
        key={gridItem.inspection.id}
        layout
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
          transition: {
            type: "spring",
            stiffness: 300,
            damping: 25,
            delay: gridIndex * 0.05,
          },
        }}
        exit={{
          opacity: 0,
          scale: 0.8,
          x: -100,
          transition: {
            duration: 0.3,
          },
        }}
        className={`${getItemClass(
          gridItemsLength
        )} rounded shadow-lg overflow-hidden flex flex-col ${
          gridItemsLength === 5 && gridIndex >= 3 ? "col-span-1" : ""
        } ${isDarkMode ? "bg-gray-800" : "bg-white border border-phoenix-200"}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <InspectionCardHeader
          inspection={gridItem.inspection}
          isDarkMode={isDarkMode}
          updatingStatus={updatingStatus}
          onOpenDetail={onOpenDetail}
          onDownloadReport={onDownloadReport}
          onConfirmClose={onConfirmClose}
        />

        <InspectionCardTabs
          sheetDetails={gridItem.sheetDetails}
          activeTab={gridItem.activeTab}
          isDarkMode={isDarkMode}
          onTabChange={onTabChange}
        />

        <InspectionCardContent
          currentSheet={gridItem.sheetDetails[gridItem.activeTab]}
          isDarkMode={isDarkMode}
          contentRef={contentRef}
          onScroll={onScroll}
        />

        <InspectionCardFooter
          sheetDetailsLength={gridItem.sheetDetails.length}
          timeUntilTabSwitch={gridItem.timeUntilTabSwitch}
          isDarkMode={isDarkMode}
        />
      </motion.div>
    );
  }
);

InspectionCard.displayName = "InspectionCard";
